import classnames from 'classnames';
import { FixtureMatchStatus } from '@stadionhq-v3/football-ui';
import {
  getMatchStatus,
  MATCH_STATUS,
  OptaMatchStatus,
} from '@stadionhq-v3/core';
import styles from './fixtureCardSecondSlot.module.scss';
import { FixtureCardSecondSlotProps } from './fixtureCardSecondSlot.types';

const FixtureCardSecondSlot = ({
  nativeProps,
  classNames,
  fixtureProps,
  additionalScoreSeparator = '-',
}: FixtureCardSecondSlotProps) => {
  const matchStatusGroup = getMatchStatus(
    fixtureProps?.matchStatus as OptaMatchStatus
  );
  const isPostmatch = matchStatusGroup === MATCH_STATUS.POST_MATCH;
  const isPreMatch = matchStatusGroup === MATCH_STATUS.PRE_MATCH;

  // Round is rendered as a label + value pair so the number can render bold
  // (Figma: label = Body/X-Large/Small-1 regular, value = Body/X-Large/Small-3
  // bold). Listing transform doesn't populate fixtureRound yet — see V3DS-352.
  const roundLabelText = fixtureProps?.fixtureRound?.label;
  const roundValueText = fixtureProps?.fixtureRound?.value;
  const hasRound = Boolean(roundLabelText || roundValueText);

  // `!= null` covers both undefined and null — GraphQL returns null for
  // missing scalar fields, so a strict `!== undefined` check silently hides
  // the aggregate row even when data is present.
  const hasAggregateScores =
    fixtureProps?.home.aggregateScore != null &&
    fixtureProps?.away.aggregateScore != null;
  const hasPenaltyScore =
    fixtureProps?.home.penaltyScore != null &&
    fixtureProps?.away.penaltyScore != null &&
    isPostmatch;

  // Aggregate score is rendered inside .scoreIndicator (under the match
  // score) — only penalty lives in the additional-scores row.
  const hasAdditionalScores = Boolean(hasPenaltyScore);

  const classes = classnames(
    [styles.tokens, styles.fixtureCardSecondSlot],
    classNames
  );

  return (
    <div
      {...nativeProps}
      className={classes}
      data-component="FixtureCardSecondSlot"
    >
      <div className={styles.centerContainer}>
        <div className={styles.matchUpContainer}>
          <div className={styles.homeTeam}>
            {fixtureProps?.home.clubCrest && (
              <span className={styles.teamCrest}>
                {fixtureProps.home.clubCrest}
              </span>
            )}
            {fixtureProps?.home.clubName && (
              <span
                className={classnames(
                  styles.teamName,
                  fixtureProps.home.isClientTeam && styles.clientTeamStyling
                )}
              >
                {fixtureProps.home.clubName}
              </span>
            )}
          </div>

          <div className={styles.scoreIndicator}>
            {isPreMatch ? (
              // Figma: a literal "V" sits between the crests on XL fixture
              // cards (P.LTR.Sub-headings.X-Large.Sub-heading-3). Hidden on
              // mobile via the .vsLabel rule.
              <span className={styles.vsLabel}>V</span>
            ) : (
              fixtureProps?.matchStatus && (
                <>
                  <FixtureMatchStatus
                    matchPeriod={fixtureProps.matchStatus}
                    time={fixtureProps.kickoffTime}
                    homeScore={fixtureProps.home.score}
                    awayScore={fixtureProps.away.score}
                    labels={fixtureProps.labels}
                    scoreDivider=":"
                  />
                  {hasAggregateScores && (
                    <div className={styles.scoreAggregate}>
                      <span className={styles.label}>
                        {fixtureProps?.labels?.aggregateAbbreviated}
                      </span>
                      <span className={styles.value}>
                        {fixtureProps?.home.aggregateScore}
                        {additionalScoreSeparator}
                        {fixtureProps?.away.aggregateScore}
                      </span>
                    </div>
                  )}
                </>
              )
            )}
          </div>

          <div className={styles.awayTeam}>
            {fixtureProps?.away.clubCrest && (
              <span className={styles.teamCrest}>
                {fixtureProps.away.clubCrest}
              </span>
            )}
            {fixtureProps?.away.clubName && (
              <span
                className={classnames(
                  styles.teamName,
                  fixtureProps.away.isClientTeam && styles.clientTeamStyling
                )}
              >
                {fixtureProps.away.clubName}
              </span>
            )}
          </div>
        </div>

        {/* Info block. Mobile: three stacked centred lines. Desktop: a
            single row with competition pinned left and date + stadium grouped
            on the right (justify-content: space-between). */}
        <div className={styles.detailsRow}>
          {(fixtureProps?.leagueLogo ||
            fixtureProps?.leagueTitle ||
            hasRound) && (
            <div className={styles.competitionAndRound}>
              {fixtureProps?.leagueLogo && (
                <span className={styles.leagueLogo}>
                  {fixtureProps.leagueLogo}
                </span>
              )}
              {fixtureProps?.leagueTitle && (
                <span className={styles.leagueTitle}>
                  {fixtureProps.leagueTitle}
                </span>
              )}
              {fixtureProps?.leagueTitle && hasRound && (
                <span className={styles.infoSeparator}>/</span>
              )}
              {roundLabelText && (
                <span className={styles.roundLabel}>{roundLabelText}</span>
              )}
              {roundValueText && (
                <span className={styles.roundValue}>{roundValueText}</span>
              )}
            </div>
          )}

          <div className={styles.metaRight}>
            {(fixtureProps?.kickoffTime || fixtureProps?.date) && (
              <div className={styles.timeAndDate}>
                {fixtureProps?.kickoffTime && (
                  <span className={styles.time}>
                    {fixtureProps.kickoffTime}
                  </span>
                )}
                {fixtureProps?.kickoffTime && fixtureProps?.date && (
                  <span className={styles.infoSeparator}>/</span>
                )}
                {fixtureProps?.date && (
                  <span className={styles.date}>{fixtureProps.date}</span>
                )}
              </div>
            )}

            {fixtureProps?.matchLocation && (
              <div className={styles.matchLocation}>
                {fixtureProps.matchLocation}
              </div>
            )}
          </div>
        </div>

        {hasAdditionalScores && (
          <div className={styles.additionalScores}>
            {hasPenaltyScore && (
              <div className={styles.penaltyScore}>
                <span className={styles.label}>
                  {fixtureProps?.labels?.penaltiesAbbreviated}
                </span>
                <span className={styles.value}>
                  {fixtureProps?.home.penaltyScore}
                  {additionalScoreSeparator}
                  {fixtureProps?.away.penaltyScore}
                </span>
              </div>
            )}
          </div>
        )}

        {fixtureProps?.metaMessagingCopy && (
          <div className={styles.metaMessaging}>
            {fixtureProps.metaMessagingIcon && (
              <span className={styles.metaMessagingIcon}>
                {fixtureProps.metaMessagingIcon}
              </span>
            )}
            <span className={styles.metaMessagingCopy}>
              {fixtureProps.metaMessagingCopy}
            </span>
          </div>
        )}

        {fixtureProps?.broadcasters && (
          <div className={styles.broadcasters}>{fixtureProps.broadcasters}</div>
        )}
      </div>
    </div>
  );
};

export default FixtureCardSecondSlot;
